/* ----------------------------------------------------
   Documore Integrity — practice domains and capabilities.
   Single source of truth for the Capabilities index,
   the capability pages (/services/:slug) and homepage links.
   Commercial terms (pricing, guarantees, samples) live in
   statements of work, not on the site.
-----------------------------------------------------*/

export const CONTACT_EMAIL = "hello@documore.com";

export const pillars = [
  {
    key: "data",
    name: "Data Integrity",
    promise: "Records that agree with each other, and with reality.",
    description:
      "Revenue, customer and supplier records decay as they move between systems and sources. We reconcile them against independent sources and document every change, so teams act on records that can be trusted.",
  },
  {
    key: "decision",
    name: "Decision Integrity",
    promise: "Inputs verified before you quote, pay or underwrite.",
    description:
      "Commercial decisions rest on documents: bills of materials, drawings, invoices, submissions. We convert them into verified data, reconcile them to source, and surface every discrepancy before it becomes a cost.",
  },
  {
    key: "agentic",
    name: "Agentic Integrity",
    promise: "AI that says and does what it should.",
    description:
      "Chatbots, voice agents and retrieval systems now speak and act for the business. We validate them against your own policies and sources, measure where they fail, and trace each failure to its cause.",
  },
];

export const method = [
  { num: "01", title: "Cross-Source Comparison", text: "Every record, document or response is compared against independent sources of the same truth." },
  { num: "02", title: "Mismatch Detection", text: "Every disagreement is isolated, classified and traced to its origin. Nothing is guessed or silently corrected." },
  { num: "03", title: "Decision Safeguards", text: "Findings are delivered against a standard agreed in advance, so each decision rests on documented evidence." },
];

export const offers = [
  /* ---------------- DATA INTEGRITY ---------------- */
  {
    slug: "lead-list-integrity",
    pillar: "data",
    name: "Lead Integrity Verification",
    summary: "Prospect data verified against independent sources before outreach.",
    headline: "Your lead list says they work there. Does anything else agree?",
    lede:
      "Prospect lists decay quickly: duplicates, undeliverable addresses, contacts who have changed roles, and existing customers mixed into cold outreach. We verify every record against two independent email verifiers, your ideal customer profile, your customer base and current employment data, then rank what remains with the reason for every decision.",
    risk: [
      "A material share of purchased prospect data is duplicated, undeliverable or outside your profile",
      "Outreach reaches contacts who left the company months ago",
      "Existing customers receive cold sequences",
      "Sending reputation suffers from bounces and complaints",
    ],
    inputs: [
      "Your prospect list (CSV or XLSX); no CRM access required",
      "Your ideal customer profile: roles, industries, company size, geography",
      "Optional: customer or do-not-contact domains for suppression",
    ],
    deliverables: [
      "Every record tiered A, B or C, with the evidence behind each score",
      "Two-verifier email status: valid, catch-all, unknown or invalid",
      "Work and personal email classification; phone line-type verification",
      "Current-employment confirmation for priority records",
      "A removal register with a reason code for every record excluded",
    ],
    method: [
      "Data profile: structure, completeness and duplicate exposure",
      "Calibration on a 300-record sample; scoring rules reviewed with you",
      "Rules approved in writing and locked before the full run",
      "Full verification, delivered against the agreed standard",
    ],
    timeline: "1–2 business days after rules are approved",
    faqs: [
      { q: "What is lead integrity verification?", a: "Lead integrity verification checks every record in a prospect list against independent sources before outreach: two email verifiers, your ideal customer profile, your existing customers and current employment data. Records that fail are removed with a reason code, and the rest are ranked A, B or C with the evidence behind each score." },
      { q: "How is this different from an email verifier?", a: "An email verifier only tells you whether a mailbox accepts mail. It cannot tell you whether the person still works at the company, whether they fit your profile, or whether they are already a customer. Lead integrity verification combines two verifiers with deduplication, profile scoring, suppression and employment checks, and documents every decision." },
      { q: "Do you need access to our CRM?", a: "No. The engagement works from the prospect list and, optionally, a list of customer domains to suppress." },
      { q: "Can catch-all emails still bounce?", a: "Yes. A catch-all server accepts every address, so no verifier can confirm that a specific mailbox exists. These records are flagged as risky, and you decide before the run whether to retain or exclude them." },
      { q: "How long does verification take?", a: "A data profile and a 300-record calibration come first. Once the scoring rules are approved, the full list is delivered within 1 to 2 business days." },
    ],
  },
  {
    slug: "crm-dedupe",
    pillar: "data",
    name: "CRM Record Reconciliation",
    summary: "One verified record per customer, reconciled inside your CRM.",
    headline: "One customer, one record, with every activity intact.",
    lede:
      "Duplicate and inconsistent CRM records distort reporting, routing and forecasting. We identify duplicate groups from a key-fields export, normalize the fields that matter, and deliver a merge plan your CRM applies natively, preserving activity history and associations.",
    risk: [
      "Duplicate contacts and companies distort pipeline and revenue reporting",
      "Teams contact the same accounts in parallel",
      "Unreconciled records undermine confidence in the CRM itself",
    ],
    inputs: [
      "A key-fields export of contacts and companies; deals, notes and activities stay in your CRM",
      "Agreed match keys and survivorship rules",
    ],
    deliverables: [
      "A merge plan: the surviving record and merged records for every duplicate group",
      "Field updates keyed to existing record IDs; nothing is re-imported",
      "Test, placeholder and invalid records identified",
      "Prevention settings for HubSpot or Salesforce",
    ],
    method: [
      "Duplicate exposure measured on a sample",
      "Match keys and survivorship rules agreed",
      "Full reconciliation and merge plan delivered",
      "Merges applied inside the CRM, by your team or under temporary API access",
    ],
    timeline: "5 business days from a complete export",
    faqs: [
      { q: "Will merging duplicates lose activity history?", a: "No. Records are never re-imported. The merge plan and field updates are keyed to existing record IDs, and merges run inside your CRM with its native merge function, which retains activities and associations." },
      { q: "What data is required from the CRM?", a: "A key-fields export of contacts and companies: record ID, name, email, phone, company, domain, owner, dates and association IDs. Deals, notes and activities remain in the CRM." },
      { q: "Which CRMs are supported?", a: "HubSpot and Salesforce most often, and any CRM that can export records with their IDs as CSV or XLSX." },
    ],
  },
  {
    slug: "vendor-master-cleanup",
    pillar: "data",
    name: "Vendor Master Reconciliation",
    summary: "A supplier master you can pay and audit against.",
    headline: "Duplicate vendors are duplicate payments waiting to happen.",
    lede:
      "Vendor masters accumulate duplicates, inactive suppliers and incomplete records over years of operation. We classify every vendor with documented evidence, without requiring bank details or tax identifiers.",
    risk: [
      "Duplicate vendor records create duplicate-payment exposure",
      "Inactive suppliers obscure the active vendor base",
      "Missing tax, address and terms data surfaces during audit",
    ],
    inputs: [
      "A vendor master export with bank columns removed and tax IDs reduced to a yes/no flag",
      "Optional: 12 months of AP totals per vendor",
    ],
    deliverables: [
      "A keep, merge, deactivate or remediate decision for every vendor, with evidence",
      "A data-quality register per vendor",
      "Potential duplicate-payment analysis",
      "A vendor onboarding data standard",
    ],
    method: [
      "Duplicate exposure measured across the full master",
      "Classification with documented match evidence",
      "Review and sign-off by your controller",
    ],
    timeline: "10 business days",
    faqs: [
      { q: "Do you need vendor bank details?", a: "No. Bank account columns are removed before export and tax ID values are replaced with a yes/no flag. Duplicates are identified from names, addresses, contacts and AP history." },
      { q: "Which ERPs are supported?", a: "Any ERP that exports its vendor master, including NetSuite, Microsoft Dynamics, SAP Business One, Sage and QuickBooks Enterprise." },
      { q: "What if duplicate exposure is low?", a: "Exposure is measured before the full engagement. If it is low, we say so, and the full reconciliation is not recommended." },
    ],
  },

  /* ---------------- DECISION INTEGRITY ---------------- */
  {
    slug: "bom-risk-scan",
    pillar: "decision",
    name: "BOM Risk Verification",
    summary: "Supply, lifecycle and cost risk verified before you quote or build.",
    headline: "Quote and build without surprise obsolete parts or stockouts.",
    lede:
      "Every line of a bill of materials verified against authorized distributor data: availability, price at your build quantity, lead time and lifecycle status, with each risk flagged before commitments are made.",
    risk: [
      "End-of-life parts surface after a quote is issued",
      "Single-source and low-stock components delay builds",
      "Invalid part numbers pass through unchecked",
    ],
    inputs: ["A bill of materials (up to 300 lines) with manufacturer part numbers and build quantity"],
    deliverables: [
      "Per-line availability, price at quantity, lead time and lifecycle status",
      "Risk classification: end-of-life, single-source, low stock",
      "Extended BOM cost and an unresolved part-number register",
      "Candidate alternates for at-risk parts, for engineering approval",
    ],
    method: [
      "Part numbers validated",
      "Distributor data retrieved and time-stamped",
      "Risk report delivered; one re-verification within 30 days",
    ],
    timeline: "24 business hours",
    faqs: [
      { q: "What does BOM risk verification check?", a: "For every line with a manufacturer part number: availability at DigiKey and Mouser, price at your build quantity, lead time and lifecycle status (Active, NRND, EOL or Obsolete), with risk flags for end-of-life, single-source and low-stock parts." },
      { q: "How large a BOM can be verified?", a: "Standard verification covers BOMs up to 300 lines within 24 business hours. Larger BOMs are scoped at intake." },
      { q: "Are replacement parts recommended?", a: "Candidate alternates are identified for at-risk parts and flagged for your engineer's approval. Nothing is substituted automatically." },
    ],
  },
  {
    slug: "bom-extraction",
    pillar: "decision",
    name: "BOM Data Verification",
    summary: "Customer BOMs converted to verified, quote-ready data.",
    headline: "Every customer BOM, verified and in your format.",
    lede:
      "Customer BOMs arrive as PDFs, scans and inconsistent spreadsheets. We deliver each one in your quoting template, with quantities reconciled to reference designators and every unreadable line recorded as an exception rather than guessed.",
    risk: [
      "Inconsistent customer formats slow every quote",
      "Manual re-keying introduces errors into costing",
      "Unreadable lines are guessed rather than escalated",
    ],
    inputs: ["Customer BOMs in any format", "Your quoting template (mapped once)"],
    deliverables: [
      "A structured BOM per file in your template, ready to import",
      "Quantity-to-reference-designator reconciliation; DNP parts identified",
      "An exceptions register for illegible or ambiguous lines",
    ],
    method: ["Template mapped once", "BOMs received", "Verified files delivered the next business day"],
    timeline: "Next business day for legible BOMs up to 200 lines",
    faqs: [
      { q: "What does quote-ready mean?", a: "One row per part in your quoting template, with quantity reconciled to reference designators, do-not-populate parts identified, and consistent units and manufacturer names, ready to import into your quoting system or ERP. It is not a priced quote." },
      { q: "Can scanned or PDF BOMs be processed?", a: "Yes. Lines that cannot be read reliably are recorded as exceptions, never guessed." },
      { q: "How accurate is the output?", a: "Field accuracy of at least 98% on a sample you verify, with line counts always matching the source." },
    ],
  },
  {
    slug: "harness-drawing-extraction",
    pillar: "decision",
    name: "Harness Data Verification",
    summary: "Harness drawings converted to reconciled wire and component data.",
    headline: "Quote harness work from a verified from-to list.",
    lede:
      "Harness and cable drawings converted into structured wire and component data, with counts reconciled to the drawing's own tables and every ambiguity escalated to your engineer.",
    risk: [
      "Manual transcription of drawings is slow and error-prone",
      "Wire and connector counts drift from the drawing",
      "Ambiguous items are resolved by assumption",
    ],
    inputs: ["Harness or cable drawing (PDF)", "Your quoting sheet format"],
    deliverables: [
      "A from-to wire list: wire, connectors and pins, gauge, color, length",
      "Connector, terminal and seal register",
      "An exceptions register per drawing",
    ],
    method: ["Drawing received", "Data extracted and reconciled to drawing tables", "Verified lists delivered the next business day"],
    timeline: "Next business day for drawings up to 100 wires",
    faqs: [
      { q: "What is a from-to wire list?", a: "A table listing each wire with its source connector and pin, destination connector and pin, gauge, color and length: the format used to quote and build wire harnesses." },
      { q: "What happens when a drawing is unclear?", a: "Ambiguous items are escalated to your engineer rather than assumed, and wire and connector counts are reconciled to the drawing's own tables." },
      { q: "What is the turnaround?", a: "Next business day for drawings up to 100 wires. Larger drawings are scoped at intake." },
    ],
  },
  {
    slug: "insurance-submission-extraction",
    pillar: "decision",
    name: "Submission Data Verification",
    summary: "Submissions and loss runs reconciled before they are marketed.",
    headline: "Every submission and loss run, verified before it goes to market.",
    lede:
      "ACORD forms, statements of values and loss runs converted into structured data in your intake template, with loss totals reconciled to source and missing information identified before it delays placement.",
    risk: [
      "Manual re-keying of submissions consumes underwriting time",
      "Loss totals fail to reconcile to carrier runs",
      "Missing information surfaces late in placement",
    ],
    inputs: ["Submission packages (PDF)", "Your intake template", "A mutual NDA, executed first"],
    deliverables: [
      "Structured submission data in your template",
      "Loss history normalized by policy year, line and claim",
      "A missing-information register per submission",
    ],
    method: ["NDA and template mapping", "Submissions received", "Verified data delivered the same business day"],
    timeline: "Same business day for up to 5 submissions received by noon ET",
    faqs: [
      { q: "Which documents are supported?", a: "ACORD application forms, statements of values and carrier loss runs, delivered as structured data in your agency's intake template." },
      { q: "How is loss run accuracy verified?", a: "Loss history is normalized by policy year, line and claim, and totals are reconciled to the loss run's own totals. Any total that does not reconcile is corrected before delivery." },
      { q: "How is client data protected?", a: "A mutual NDA is executed before any documents are shared. Data is used only for the engagement and deleted within 30 days of acceptance." },
    ],
  },

  /* ---------------- AGENTIC INTEGRITY ---------------- */
  {
    slug: "chatbot-reliability-audit",
    pillar: "agentic",
    name: "AI Response Validation",
    summary: "Customer-facing AI validated against your own policies and sources.",
    headline: "Know where your AI contradicts your own policies.",
    lede:
      "We validate a customer-facing chatbot across 50 structured scenarios, testing every response against your published policies, pricing and terms. The standard assessment requires no system access or internal data.",
    risk: [
      "No one has a reliable view of what the assistant tells customers",
      "Incorrect policy or pricing answers create liability and support load",
      "Rigorous testing competes with every other priority on the team",
    ],
    inputs: ["The URL of your live assistant", "Optional: priority intents and policy references"],
    deliverables: [
      "Fifty scenarios across accuracy, policy consistency, dead ends, escalation and tone",
      "Every failure severity-rated, with transcript and remediation",
      "Each failure re-run and classified as consistent or intermittent",
      "Re-validation of High and Critical findings within 30 days",
    ],
    method: [
      "Scenario design against your published policies",
      "Validation against the live assistant",
      "Findings report and executive walkthrough",
      "Re-validation after remediation",
    ],
    scopeNote:
      "Adversarial testing (prompt injection, jailbreak, data leakage) is performed only under written authorization, preferably against a staging environment.",
    timeline: "3 business days",
    faqs: [
      { q: "What does AI response validation test?", a: "Fifty structured scenarios covering answer accuracy against your published information, consistency with your policies, pricing and terms, dead ends, escalation to a human, out-of-scope requests and tone." },
      { q: "Is access to our systems required?", a: "No. The standard assessment tests your live public assistant and requires no logins or internal data. Adversarial security testing is scoped separately and requires written authorization." },
      { q: "AI answers vary between runs. How are findings confirmed?", a: "Every failure is re-run and classified as consistent or intermittent, and every finding includes its timestamped transcript." },
      { q: "What counts as a material issue?", a: "A response that contradicts your published policy, pricing or terms, gives incorrect factual information, fails to escalate a defined case, or exposes information not intended for the user." },
    ],
  },
  {
    slug: "voice-agent-scorecard",
    pillar: "agentic",
    name: "Voice Agent Validation",
    summary: "Voice agent performance measured against an agreed standard.",
    headline: "Know which calls your voice agent wins, loses and mishandles.",
    lede:
      "We evaluate your voice agent's calls against a rubric agreed in advance (goal completion, accuracy, compliance and escalation) and consolidate failures into the remediations that matter most.",
    risk: [
      "Call volume exceeds any team's capacity to review",
      "Quality judgments remain subjective without a standard",
      "Remediation priorities are unclear",
    ],
    inputs: ["Call recordings or transcripts (up to 200 or 500 calls)", "Call objectives and escalation rules"],
    deliverables: [
      "Every call evaluated against the agreed rubric",
      "Failure patterns consolidated and quantified",
      "The ten highest-impact remediations, ranked",
      "Re-evaluation of 50 calls after remediation",
    ],
    method: ["Rubric agreed", "20-call calibration with your team", "Full evaluation and report"],
    scopeNote: "Healthcare or other regulated calls only under an executed data agreement.",
    timeline: "5 business days",
    faqs: [
      { q: "How are voice agent calls evaluated?", a: "Against a rubric agreed before evaluation begins: goal completion, accuracy, compliance and escalation handling. A 20-call calibration is completed first so that scoring reflects your standards." },
      { q: "What is required from us?", a: "Call recordings or transcripts, up to 200 or 500 calls, together with call objectives, scripts and escalation rules." },
      { q: "Can healthcare calls be evaluated?", a: "Only under an executed data agreement such as a business associate agreement." },
    ],
  },
  {
    slug: "rag-retrieval-tune-up",
    pillar: "agentic",
    name: "Retrieval Integrity Assessment",
    summary: "Measured evidence of how your RAG system finds the truth.",
    headline: "Measured evidence of which retrieval design returns correct answers on your data.",
    lede:
      "We build a reference test set from real questions and evaluate chunking, embedding and retrieval strategies against it, so every failure is attributed to the layer that caused it and every recommendation is backed by measurement.",
    risk: [
      "Chunking and retrieval parameters were set by assumption",
      "Answer quality is not measured",
      "Failures cannot be attributed to retrieval or generation",
    ],
    inputs: [
      "A corpus sample or staging index access",
      "20 to 100 representative user questions",
      "The current chunking, embedding and retrieval configuration",
    ],
    deliverables: [
      "A 100-question reference test set, retained by you",
      "Five chunking strategies compared on recall, precision and answer quality",
      "Every failure attributed: chunking, retrieval, ranking or generation",
      "A recommended configuration with reproducible evaluation code",
    ],
    method: ["Test set agreed", "Strategies evaluated", "Report, code and re-assessment after changes"],
    timeline: "7 business days",
    faqs: [
      { q: "What is a retrieval integrity assessment?", a: "An evaluation of how a retrieval-augmented generation system finds information. A reference test set is built from real questions, and chunking, embedding and retrieval strategies are compared on your own data to identify the configuration that returns correct answers." },
      { q: "Which evaluation methods are used?", a: "Retrieval metrics such as recall@k, precision and MRR, and answer-quality metrics using frameworks such as RAGAS and DeepEval, with every failure attributed to chunking, retrieval, ranking or generation." },
      { q: "Do we retain the test set?", a: "Yes. The reference test set and evaluation code remain with you, so the assessment can be repeated after every change." },
    ],
  },
];

export const offersByPillar = (key) => offers.filter((o) => o.pillar === key);
export const findOffer = (slug) => offers.find((o) => o.slug === slug);
